import { Menu, ActionIcon, rem, CopyButton, Anchor } from "@mantine/core"
import {
  IconCopy,
  IconDotsVertical,
  IconDownload,
  IconEdit,
  IconTrash,
} from "@tabler/icons-react"
import { WebCalendarProfileDto } from "../../../../../@client/Theasoft_Shared_Contracts_WebCalendar"
import { useTranslation } from "react-i18next"
import { useIsSmallScreen } from "../../../../../utils/useIsSmallScreen"
import { useInvalidate } from "@/typed-client/hooks/use-invalidate"
import { useDeletePersonalWebCalendarProfile } from "@/orval/gen"

interface Props {
  profile: WebCalendarProfileDto
  onEditProfileClick: (profile: WebCalendarProfileDto) => void
}

export function CopyCalendarProfileLinkButton({
  profile,
  component: component,
}: {
  profile: WebCalendarProfileDto
  component: "Button" | "MenuItem"
}) {
  return (
    <CopyButton value={profile.externalLink}>
      {({ copied, copy }) =>
        component == "MenuItem" ? (
          <Menu.Item
            onClick={copy}
            leftSection={<IconCopy size={14} />}
            color={copied ? "teal" : "blue"}
            variant="subtle"
          >
            <ProfileLinkAnchor
              copied={copied}
              copy={copy}
              link={profile.externalLink}
            />
          </Menu.Item>
        ) : (
          <ProfileLinkAnchor
            copied={copied}
            copy={copy}
            link={profile.externalLink}
          />
        )
      }
    </CopyButton>
  )
}

function ProfileLinkAnchor({
  copied,
  link,
  copy,
}: {
  copied: boolean
  link: string
  copy: () => void
}) {
  const { t } = useTranslation("app_server")
  return (
    <Anchor
      c={copied ? "teal" : "blue"}
      size="xs"
      onClick={(e) => {
        e.preventDefault()
        copy()
      }}
      href={link}
    >
      {copied ? t("CopiedLink") : t("CopyLink")}
    </Anchor>
  )
}

export function MoreMenu({ profile, onEditProfileClick }: Props) {
  const { t } = useTranslation("app_server")
  const isMobile = useIsSmallScreen()
  const invalidate = useInvalidate()
  const deleteProfile = useDeletePersonalWebCalendarProfile({
    mutation: {
      onSuccess: () => {
        invalidate("/api/WebCalendar/GetProfiles")
      },
    },
  })

  return (
    <Menu withinPortal position="bottom-end" shadow="sm">
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray">
          <IconDotsVertical style={{ width: rem(16), height: rem(16) }} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>
        {isMobile ? (
          <CopyCalendarProfileLinkButton
            profile={profile}
            component="MenuItem"
          />
        ) : null}

        <Menu.Item
          onClick={() => {
            window.location.href = profile.internalLink
          }}
          leftSection={
            <IconDownload
              style={{
                width: rem(14),
                height: rem(14),
              }}
            />
          }
        >
          {t("Download")}
        </Menu.Item>
        {profile.allowEditAndDelete && (
          <>
            <Menu.Item
              leftSection={
                <IconEdit
                  style={{
                    width: rem(14),
                    height: rem(14),
                  }}
                />
              }
              onClick={() => onEditProfileClick(profile)}
            >
              {t("Edit")}
            </Menu.Item>
            <Menu.Item
              onClick={() =>
                deleteProfile.mutate({
                  data: profile.webCalendarProfileId,
                })
              }
              leftSection={
                <IconTrash
                  style={{
                    width: rem(14),
                    height: rem(14),
                  }}
                />
              }
              color="red"
            >
              {t("Delete")}
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}
